import React, { useEffect, useMemo, useRef, useState } from 'react';
import SearchBar from './SearchBar';

const speakerColorPalette = [
  '#7a8fa6',
  '#b08a86',
  '#8a9e7e',
  '#9a8db8',
  '#b5899e',
  '#b09a6e',
];

const formatTranscriptTimestamp = (deciSeconds) => {
  const safe = typeof deciSeconds === 'number' ? deciSeconds : 0;
  const totalSeconds = Math.max(0, Math.floor(safe / 10));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
  const seconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  if (hours > 0) return `${String(hours).padStart(2, '0')}:${minutes}:${seconds}`;
  return `${minutes}:${seconds}`;
};

const normalizeTranscriptDisplayText = (text) => text.replace(/\s+/g, ' ').trim();
const hasMeaningfulTranscriptText = (text) =>
  /[A-Za-z0-9]/.test(normalizeTranscriptDisplayText(text));

const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const highlightText = (text, term) => {
  if (!term) return [text];
  const escaped = escapeRegex(term);
  const regex = new RegExp(escaped, 'gi');
  let lastIndex = 0;
  const result = [];
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) result.push(text.slice(lastIndex, match.index));
    result.push(
      <span key={`hl-${key++}`} className="bg-yellow-200 inline whitespace-normal">
        {match[0]}
      </span>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) result.push(text.slice(lastIndex));
  return result;
};

const getSpeakerInitials = (speaker) => {
  if (!speaker) return '?';
  const words = speaker.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  return speaker.substring(0, 2).toUpperCase();
};

const RichTranscript = ({ transcript, onGoToTimeStamp, initialSearchTerm, searchText }) => {
  const [searcherResult, setSearcherResult] = useState([]);
  const [paragraphPosition, setParagraphPosition] = useState(0);
  const [searchTerm, setSearchTerm] = useState(null);
  const containerRef = useRef(null);
  const speakerColorMap = useRef({});

  useEffect(() => {
    speakerColorMap.current = {};
  }, [transcript]);

  const speakerGroups = useMemo(() => {
    const speakerTranscript = transcript?.speakerTranscript;
    if (!speakerTranscript || speakerTranscript.length === 0) return [];

    const groups = [];
    for (let index = 0; index < speakerTranscript.length; index++) {
      const segment = speakerTranscript[index];
      if (!hasMeaningfulTranscriptText(segment.text)) continue;

      const previous = groups[groups.length - 1];
      const previousKey = previous?.speakerKey ?? '';
      const segKey = (segment.speaker ?? '').trim().toLowerCase();

      if (previous && previousKey === segKey) {
        previous.segments.push({ index, segment });
        continue;
      }

      groups.push({
        speaker: segment.speaker,
        speakerKey: segKey,
        segments: [{ index, segment }],
      });
    }
    return groups;
  }, [transcript]);

  const getSpeakerColor = (speaker) => {
    const key = (speaker ?? '').trim().toLowerCase();
    const map = speakerColorMap.current;
    if (!map[key]) {
      map[key] = speakerColorPalette[Object.keys(map).length % speakerColorPalette.length];
    }
    return map[key];
  };

  const scrollToParagraph = (paragraphNumber) => {
    const el = document.getElementById(`p-${paragraphNumber}`);
    const container = containerRef.current;
    if (el && container) {
      container.scrollTo({ top: el.offsetTop - container.offsetTop, behavior: 'smooth' });
    }
  };

  const handleSearch = (term) => {
    if (!term) {
      setSearcherResult([]);
      return;
    }
    const lowerTerm = term.toLowerCase();
    if (transcript?.wordTranscript?.length > 0) {
      const result = transcript.wordTranscript.reduce((acc, p, index) => {
        const searchWords = term.split(' ');
        const positions = [];
        for (let i = 0; i < p.length - searchWords.length + 1; i++) {
          let found = true;
          const tempPositions = [];
          for (let j = 0; j < searchWords.length; j++) {
            if (
              !p[i + j].word
                .toLowerCase()
                .replace(/[^\w\s]/g, '')
                .startsWith(searchWords[j].toLowerCase().replace(/[^\w\s]/g, ''))
            ) {
              found = false;
              break;
            }
            tempPositions.push(i + j);
          }
          if (found) positions.push(...tempPositions);
        }
        if (positions.length > 0) acc.push({ paragraphIndex: index, positions });
        return acc;
      }, []);
      setParagraphPosition(0);
      setSearcherResult(result);
      return;
    }

    if (transcript?.speakerTranscript?.length > 0) {
      const result = transcript.speakerTranscript.reduce((acc, segment, index) => {
        if (segment.text.toLowerCase().includes(lowerTerm)) {
          acc.push({ paragraphIndex: index, positions: [] });
        }
        return acc;
      }, []);
      setParagraphPosition(0);
      setSearcherResult(result);
    }
  };

  useEffect(() => {
    if (searchText) {
      setSearchTerm(searchText);
      handleSearch(searchText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  useEffect(() => {
    if (initialSearchTerm && transcript && searchTerm === null) {
      setSearchTerm(initialSearchTerm);
      handleSearch(initialSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSearchTerm, transcript]);

  useEffect(() => {
    const current = searcherResult[paragraphPosition];
    if (!current) return;
    scrollToParagraph(current.paragraphIndex);

    if (transcript) {
      let timestamp = 0;
      if (transcript.wordTranscript?.length > 0 && current.positions?.length > 0) {
        const wordIdx = current.positions[0];
        const word = transcript.wordTranscript[current.paragraphIndex]?.[wordIdx];
        if (word) timestamp = word.startTime;
      } else if (transcript.speakerTranscript?.length > 0) {
        const segment = transcript.speakerTranscript[current.paragraphIndex];
        if (segment) timestamp = segment.startTime;
      }
      if (timestamp > 0 && onGoToTimeStamp) onGoToTimeStamp(timestamp);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paragraphPosition, searcherResult]);

  const handleChangeParagraphLocation = (direction) => {
    if (direction === 'down' && paragraphPosition < searcherResult.length - 1) {
      setParagraphPosition(paragraphPosition + 1);
    } else if (direction === 'up' && paragraphPosition > 0) {
      setParagraphPosition(paragraphPosition - 1);
    }
  };

  const hasTranscript =
    (transcript?.wordTranscript?.length ?? 0) > 0 ||
    (transcript?.speakerTranscript?.length ?? 0) > 0;

  return (
    <div>
      <div className="mb-4">
        <h2 className="text-lg font-medium mb-3 text-stone-800">Video Transcript</h2>
        <SearchBar
          handleSearch={handleSearch}
          counter={searcherResult.length}
          location={paragraphPosition + 1}
          onChangeLocation={handleChangeParagraphLocation}
          placeholder="Search..."
          search={searchTerm}
          setSearch={setSearchTerm}
          autoSearch
        />
      </div>
      <div className="space-y-3 h-[calc(100vh-320px)] overflow-y-auto" ref={containerRef}>
        {!hasTranscript && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
            No transcript available for this hearing yet.
          </div>
        )}

        {transcript?.wordTranscript?.length > 0 &&
          transcript.wordTranscript.map((paragraph, i) => (
            <React.Fragment key={i}>
              {paragraph.length > 0 && (
                <p className="w-full flex flex-wrap" key={`p-${i}`} id={`p-${i}`}>
                  {paragraph.map((wordObj, j) => {
                    const isHighlighted = searcherResult?.some(
                      (item) => item.paragraphIndex === i && item.positions?.includes(j)
                    );
                    return (
                      <span
                        key={`span-${i}-${j}`}
                        id={`span-${i}-${j}`}
                        role="button"
                        tabIndex={0}
                        title={`${formatTranscriptTimestamp(wordObj.startTime)} - ${formatTranscriptTimestamp(wordObj.endTime)}`}
                        className={`whitespace-pre-wrap hover:bg-blue-100 cursor-pointer px-1 relative ${
                          isHighlighted ? 'bg-blue-200' : ''
                        }`}
                        onClick={() => onGoToTimeStamp && onGoToTimeStamp(wordObj.startTime)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onGoToTimeStamp && onGoToTimeStamp(wordObj.startTime);
                          }
                        }}
                      >
                        {wordObj.word}
                      </span>
                    );
                  })}
                </p>
              )}
            </React.Fragment>
          ))}

        {speakerGroups.map((group) => {
          const firstSegment = group.segments[0];
          const groupKey = firstSegment ? `speaker-group-${firstSegment.index}` : group.speaker;
          const segmentColor = getSpeakerColor(group.speaker);

          return (
            <div className="flex gap-3" key={groupKey}>
              <div className="flex flex-col items-center gap-1.5 pt-3">
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold text-white shrink-0"
                  style={{ backgroundColor: segmentColor }}
                >
                  {getSpeakerInitials(group.speaker)}
                </div>
                <div
                  className="w-1 rounded-full flex-1"
                  style={{ backgroundColor: segmentColor, opacity: 0.4 }}
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 overflow-hidden rounded-2xl border border-stone-200/60 bg-[#fdfbf7] shadow-sm shadow-stone-200/40">
                <div className="px-4 py-3 text-sm leading-8 text-stone-700 whitespace-normal">
                  {group.segments.map(({ index, segment }) => {
                    const isHighlighted = searcherResult?.some(
                      (item) => item.paragraphIndex === index
                    );
                    const startDeciseconds =
                      typeof segment.startTime === 'number' ? segment.startTime : 0;
                    const endDeciseconds =
                      typeof segment.endTime === 'number' ? segment.endTime : startDeciseconds;
                    const displayText = normalizeTranscriptDisplayText(segment.text);
                    if (!displayText) return null;

                    const segmentHighlightClass = isHighlighted
                      ? 'bg-blue-50/80 shadow-[inset_0_0_0_1px_rgba(191,219,254,0.9)]'
                      : 'hover:bg-stone-50/70';

                    return (
                      <React.Fragment key={`span-${index}`}>
                        <span
                          id={`p-${index}`}
                          className="relative -top-20 inline-block h-0 w-0 overflow-hidden align-baseline"
                          aria-hidden="true"
                        />
                        <span
                          id={`span-${index}`}
                          role="button"
                          tabIndex={0}
                          title={`${formatTranscriptTimestamp(startDeciseconds)} - ${formatTranscriptTimestamp(endDeciseconds)}`}
                          className={`inline cursor-pointer rounded-sm px-0.5 py-0.5 transition-colors duration-150 box-decoration-clone ${segmentHighlightClass}`}
                          onClick={() => onGoToTimeStamp && onGoToTimeStamp(startDeciseconds)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onGoToTimeStamp && onGoToTimeStamp(startDeciseconds);
                            }
                          }}
                        >
                          {highlightText(displayText, searchTerm)}
                        </span>{' '}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RichTranscript;
